import { useEffect, useRef, useState } from 'react';

import Collaborators from '../../services/api/collaborators/collaborators';
import { ChargeType } from '../../types/types';

const usePlanChargeType = (companyId: string, planId?: number) => {
  const [chargeType, setChargeType] = useState<ChargeType | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const cacheRef = useRef<Map<string, ChargeType | undefined>>(new Map());
  const requestIdRef = useRef<number>(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrent = () => requestIdRef.current === requestId;

    setChargeType(undefined);

    if (!companyId || planId == null) {
      setIsLoading(false);
      setError(false);
      return;
    }

    const cacheKey = `${companyId}:${planId}`;

    if (cacheRef.current.has(cacheKey)) {
      setChargeType(cacheRef.current.get(cacheKey));
      setIsLoading(false);
      setError(false);
      return;
    }

    setIsLoading(true);
    setError(false);

    Collaborators.getCompanyContract(companyId, planId)
      .then((contract) => {
        if (!isCurrent()) return;
        cacheRef.current.set(cacheKey, contract?.type);
        setChargeType(contract?.type);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setChargeType(undefined);
        setError(true);
      })
      .finally(() => {
        if (isCurrent()) {
          setIsLoading(false);
        }
      });

    return () => {
      requestIdRef.current = requestId + 1;
    };
  }, [companyId, planId]);

  return { chargeType, isLoading, error };
};

export default usePlanChargeType;
